import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useValidateCustomServiceMutation } from '../../../../../app/Slices/SevenDayApiSlice';
import type { CustomServiceValidation } from '../../../../../app/Slices/SevenDayApiTypes';
import toast from '../../../../shared/Toast';

export const serviceKey = (label: string) =>
    label.trim().replace(/\s+/g, ' ').toLowerCase();

type PredefinedOption = { value: string; label: string };

type KnownService = {
    displayName: string;
    isPredefined: boolean;
    alreadySelected: boolean;
};

const findExactDuplicate = (
    label: string,
    predefinedOptions: PredefinedOption[],
    existingItems: string[],
): KnownService | null => {
    const key = serviceKey(label);
    if (!key) return null;

    const known = new Map<string, KnownService>();
    const predefinedValues = new Set(
        predefinedOptions.map((option) => option.value),
    );

    const register = (
        registerKey: string,
        displayName: string,
        isPredefined: boolean,
        alreadySelected = false,
    ) => {
        if (!registerKey || known.has(registerKey)) return;
        known.set(registerKey, {
            displayName,
            isPredefined,
            alreadySelected,
        });
    };

    for (const option of predefinedOptions) {
        const isSelected = existingItems.includes(option.value);
        register(serviceKey(option.value), option.label, true, isSelected);
        register(serviceKey(option.label), option.label, true, isSelected);
    }

    for (const item of existingItems) {
        const trimmed = item.trim();
        if (!trimmed) continue;

        if (predefinedValues.has(item)) {
            const match = predefinedOptions.find(
                (option) => option.value === item,
            );
            const displayName = match?.label ?? trimmed;
            register(serviceKey(item), displayName, true, true);
            register(serviceKey(displayName), displayName, true, true);
        } else {
            register(serviceKey(item), trimmed, false, true);
        }
    }

    return known.get(key) ?? null;
};

const duplicateReason = (label: string, match: KnownService): string => {
    if (match.isPredefined && !match.alreadySelected) {
        return `"${label}" is already available as a standard service. Select it from the list instead.`;
    }
    return `"${match.displayName}" is already in your services.`;
};

export type CustomServiceRejection = { label: string; reason: string };
export type CustomServiceSuggestion = {
    label: string;
    suggested: string;
    /** Pre-signed token for `suggested`, so accepting it costs no request. */
    token?: string;
};

export type CustomServiceVerdict = {
    accepted: boolean;
    /** The label to commit, cleaned up by the model when it was accepted. */
    label: string;
};

export const useCustomServiceValidation = (
    serviceType: string,
    predefinedOptions: PredefinedOption[] = [],
) => {
    const { control, setValue } = useFormContext();
    const [validateCustomService] = useValidateCustomServiceMutation();
    const existingItems = (useWatch({ name: 'services.items', control }) ??
        []) as string[];

    const [pendingLabel, setPendingLabel] = useState<string | null>(null);
    const [rejection, setRejection] = useState<CustomServiceRejection | null>(
        null,
    );
    const [suggestion, setSuggestion] =
        useState<CustomServiceSuggestion | null>(null);

    /** Tokens for services accepted in this session, keyed by service. */
    const tokensRef = useRef(new Map<string, string>());
    /** Verdicts already seen, so retyping the same entry does not cost a request. */
    const cacheRef = useRef(new Map<string, CustomServiceValidation>());

    const clearFeedback = () => {
        setRejection(null);
        setSuggestion(null);
    };

    useEffect(() => {
        setRejection(null);
        setSuggestion(null);

        if (tokensRef.current.size === 0) {
            cacheRef.current.clear();
            return;
        }

        tokensRef.current.clear();
        cacheRef.current.clear();
        setValue('services.validationTokens', []);
    }, [serviceType, setValue]);

    const acceptToken = (
        label: string,
        token: string,
    ): CustomServiceVerdict => {
        tokensRef.current.set(serviceKey(label), token);
        setValue(
            'services.validationTokens',
            Array.from(tokensRef.current.values()),
            { shouldDirty: true },
        );
        clearFeedback();
        return { accepted: true, label };
    };

    const applyVerdict = (
        label: string,
        result: CustomServiceValidation,
    ): CustomServiceVerdict => {
        if (result.status === 'valid' && result.validationToken) {
            return acceptToken(
                result.normalized.trim(),
                result.validationToken,
            );
        }

        if (result.status === 'typo' && result.suggestion) {
            setRejection(null);
            setSuggestion({
                label,
                suggested: result.suggestion,
                token: result.suggestionToken,
            });
            return { accepted: false, label };
        }

        setSuggestion(null);
        setRejection({
            label,
            reason: result.reason,
        });
        return { accepted: false, label };
    };

    const validate = async (label: string): Promise<CustomServiceVerdict> => {
        const key = serviceKey(label);

        // Already approved in this session, so no need to ask again.
        if (tokensRef.current.has(key)) {
            clearFeedback();
            return { accepted: true, label };
        }

        const duplicate = findExactDuplicate(
            label,
            predefinedOptions,
            existingItems,
        );
        if (duplicate) {
            setSuggestion(null);
            setRejection({
                label,
                reason: duplicateReason(label, duplicate),
            });
            return { accepted: false, label };
        }

        const cached = cacheRef.current.get(key);
        if (cached) return applyVerdict(label, cached);

        setPendingLabel(label);
        try {
            const result = await validateCustomService({
                serviceType,
                label,
                existingItems,
            }).unwrap();

            // Accepted entries are remembered by their token instead, so a
            // 'valid' verdict that arrived without one stays retryable.
            if (result.status !== 'valid') {
                cacheRef.current.set(key, result);
            }
            return applyVerdict(label, result);
        } catch (_error) {
            toast.error('Could not verify that service, please try again');
            return { accepted: false, label };
        } finally {
            setPendingLabel(null);
        }
    };

    const acceptSuggestion = async (): Promise<CustomServiceVerdict> => {
        if (!suggestion) return { accepted: false, label: '' };

        if (suggestion.token) {
            return acceptToken(suggestion.suggested, suggestion.token);
        }

        // No token came back, so fall back to a fresh check.
        return validate(suggestion.suggested);
    };

    return {
        validate,
        acceptSuggestion,
        validating: pendingLabel !== null,
        pendingLabel,
        rejection,
        suggestion,
        clearFeedback,
    };
};

export default useCustomServiceValidation;
